<template>
  <fieldset class="fieldset inquiry-form-config mb-4 mb-lg-5" :disabled="form.managed_by_manet">
    <legend>Anfrageformular / Buchungssystem</legend>

    <div v-if="form.managed_by_manet" class="alert alert-info">
      Um die Kontaktmöglichkeiten auf mvp.de und Ihrer Website aufeinander abgestimmt zu halten,
      verwaltet MANET diese Einstellung für Sie.
      <a :href="supportTicketUrl">Support kontaktieren</a>
    </div>

    <section
      class="inquiry-form-config__chooser"
      aria-labelledby="inquiry-form-selection-title"
    >
      <div class="inquiry-form-config__intro">
        <h3 id="inquiry-form-selection-title">Wie möchten Sie Anfragen erhalten?</h3>
        <p>Wählen Sie die passende Kontaktmöglichkeit. Details können Sie anschließend direkt darunter einrichten.</p>
      </div>

      <div class="inquiry-form-config__choice-group">
        <label
          class="inquiry-form-choice"
          :class="{'inquiry-form-choice--selected': selection === 'none'}"
        >
          <input
            v-model="selection"
            class="visually-hidden"
            type="radio"
            name="inquiry-booking-mode"
            value="none"
            @change="applySelection"
          >
          <span class="inquiry-form-choice__icon" aria-hidden="true">
            <Icon icon="fa-pro-solid:ban" />
          </span>
          <span class="inquiry-form-choice__content">
            <span class="inquiry-form-choice__title">Keine Anfragen</span>
            <span class="inquiry-form-choice__description">
              Auf der Detailseite wird keine Anfrage- oder Buchungsoption angezeigt.
            </span>
          </span>
          <Icon class="inquiry-form-choice__check" icon="fa-pro-solid:circle-check" aria-hidden="true" />
        </label>

        <label
          class="inquiry-form-choice"
          :class="{'inquiry-form-choice--selected': selection === 'external'}"
        >
          <input
            v-model="selection"
            class="visually-hidden"
            type="radio"
            name="inquiry-booking-mode"
            value="external"
            @change="applySelection"
          >
          <span class="inquiry-form-choice__icon" aria-hidden="true">
            <Icon icon="fa-pro-solid:arrow-up-right-from-square" />
          </span>
          <span class="inquiry-form-choice__content">
            <span class="inquiry-form-choice__title">
              Eigenes Buchungssystem
              <span class="badge text-bg-warning">Premium</span>
            </span>
            <span class="inquiry-form-choice__description">
              Verlinken Sie zu Ihrem bestehenden Anfrageformular oder Buchungssystem.
            </span>
          </span>
          <Icon class="inquiry-form-choice__check" icon="fa-pro-solid:circle-check" aria-hidden="true" />
        </label>

        <label
          v-if="bookingMode === 'product' && form.booking_product"
          class="inquiry-form-choice"
          :class="{'inquiry-form-choice--selected': selection === `product:${form.booking_product.id}`}"
        >
          <input
            v-model="selection"
            class="visually-hidden"
            type="radio"
            name="inquiry-booking-mode"
            :value="`product:${form.booking_product.id}`"
            @change="applySelection"
          >
          <span class="inquiry-form-choice__icon" aria-hidden="true">
            <Icon icon="fa-pro-solid:calendar-check" />
          </span>
          <span class="inquiry-form-choice__content">
            <span class="inquiry-form-choice__title">
              {{ form.booking_product.name }}
              <span class="badge text-bg-info">Von MANET verwaltet</span>
            </span>
            <span class="inquiry-form-choice__description">{{ form.booking_product.function }}</span>
          </span>
          <Icon class="inquiry-form-choice__check" icon="fa-pro-solid:circle-check" aria-hidden="true" />
        </label>

        <label
          class="inquiry-form-choice inquiry-form-choice--mvp"
          :class="{'inquiry-form-choice--selected': isMvpFormSelection}"
        >
          <input
            class="visually-hidden"
            type="radio"
            name="inquiry-booking-mode"
            value="mvp"
            :checked="isMvpFormSelection"
            @change="selectMvpFormMode"
          >
          <span class="inquiry-form-choice__icon" aria-hidden="true">
            <Icon icon="fa-pro-solid:message-lines" />
          </span>
          <span class="inquiry-form-choice__content">
            <span class="inquiry-form-choice__title">Anfrageformular auf mvp.de</span>
            <span class="inquiry-form-choice__description">
              Nutzen Sie ein vorhandenes Formular oder richten Sie ein neues für dieses Objekt ein.
            </span>
          </span>
          <Icon class="inquiry-form-choice__check" icon="fa-pro-solid:circle-check" aria-hidden="true" />
        </label>
      </div>

      <div
        v-if="isMvpFormSelection"
        id="inquiry-form-mvp-options"
        class="inquiry-form-config__mvp-forms"
      >
        <div class="inquiry-form-config__section-heading">
          <span class="inquiry-form-config__section-icon" aria-hidden="true">
            <Icon icon="fa-pro-solid:list-check" />
          </span>
          <div>
            <h4>Passendes Anfrageformular auswählen</h4>
            <p>Wählen Sie eine Vorlage oder übernehmen Sie ein bereits eingerichtetes Formular.</p>
          </div>
        </div>

        <template v-if="form.available_submission_routes.length">
          <div class="inquiry-form-config__group-label">Vorhandene Formulare</div>
          <div class="inquiry-form-config__choice-group inquiry-form-config__choice-group--forms">
            <label
              v-for="route in form.available_submission_routes"
              :key="route.id"
              class="inquiry-form-choice inquiry-form-choice--form"
              :class="{'inquiry-form-choice--selected': selection === `route:${route.id}`}"
            >
              <input
                v-model="selection"
                class="visually-hidden"
                type="radio"
                name="inquiry-form-selection"
                :value="`route:${route.id}`"
                @change="applySelection"
              >
              <span class="inquiry-form-choice__icon" aria-hidden="true">
                <Icon icon="fa-pro-solid:inbox" />
              </span>
              <span class="inquiry-form-choice__content">
                <span class="inquiry-form-choice__title">
                  {{ route.display_name }}
                  <span class="badge text-bg-light">Vorhanden</span>
                  <span v-if="route.premium" class="badge text-bg-warning">Premium</span>
                </span>
                <span class="inquiry-form-choice__description">
                  {{ routeTypeLabel(route) }} · Zustellung an {{ route.recipient }}
                </span>
              </span>
              <Icon class="inquiry-form-choice__check" icon="fa-pro-solid:circle-check" aria-hidden="true" />
            </label>
          </div>
        </template>

        <div class="inquiry-form-config__group-label">Neues Formular</div>
        <div class="inquiry-form-config__choice-group inquiry-form-config__choice-group--forms">
          <label
            v-for="template in form.available_form_templates"
            :key="template.id"
            class="inquiry-form-choice inquiry-form-choice--form"
            :class="{'inquiry-form-choice--selected': selection === `template:${template.id}`}"
          >
            <input
              v-model="selection"
              class="visually-hidden"
              type="radio"
              name="inquiry-form-selection"
              :value="`template:${template.id}`"
              @change="applySelection"
            >
            <span class="inquiry-form-choice__icon" aria-hidden="true">
              <Icon :icon="templateIcon(template)" />
            </span>
            <span class="inquiry-form-choice__content">
              <span class="inquiry-form-choice__title">
                {{ template.name }}
                <span v-if="template.premium" class="badge text-bg-warning">Premium</span>
              </span>
              <span class="inquiry-form-choice__description">{{ templateDescription(template) }}</span>
            </span>
            <Icon class="inquiry-form-choice__check" icon="fa-pro-solid:circle-check" aria-hidden="true" />
          </label>

          <label
            class="inquiry-form-choice inquiry-form-choice--form"
            :class="{'inquiry-form-choice--selected': selection === 'custom'}"
          >
            <input
              v-model="selection"
              class="visually-hidden"
              type="radio"
              name="inquiry-form-selection"
              value="custom"
              @change="applySelection"
            >
            <span class="inquiry-form-choice__icon" aria-hidden="true">
              <Icon icon="fa-pro-solid:wand-magic-sparkles" />
            </span>
            <span class="inquiry-form-choice__content">
              <span class="inquiry-form-choice__title">
                Individuelles Anfrageformular
                <span class="badge text-bg-warning">Premium</span>
              </span>
              <span class="inquiry-form-choice__description">
                Gemeinsam mit MANET entwickeln wir ein Formular für einen anderen Verwendungszweck.
              </span>
            </span>
            <Icon class="inquiry-form-choice__check" icon="fa-pro-solid:circle-check" aria-hidden="true" />
          </label>
        </div>
      </div>
    </section>

    <div v-if="selection === 'external'" class="inquiry-form-config__details mt-3">
      <div class="row g-3">
        <div class="col-12 col-lg-8">
          <label class="form-label" for="inquiry-external-booking-url">Link zum Anfrageformular / Buchungssystem</label>
          <input
            id="inquiry-external-booking-url"
            v-model.trim="externalUrl"
            class="form-control"
            type="url"
            required
            placeholder="https://..."
          >
        </div>
        <div class="col-12 col-lg-4 d-flex flex-column align-items-start justify-content-end">
          <span class="form-label">Buchungssystem</span>
          <span
            v-if="showStoredExternalSystem && externalSystem && form.external_booking_system_label"
            class="badge text-bg-secondary mb-2"
          >{{ form.external_booking_system_label }}</span>
          <small v-else class="text-muted mb-2">Wird beim Speichern automatisch erkannt.</small>
        </div>
      </div>
      <small class="text-muted">
        Der Link wird auf der Detailseite als Suchbox angeboten und über den MANET-Weiterleitungsdienst aufgerufen.
      </small>
      <PremiumFeatureHint
        class="mt-3"
        :visible="!form.premium_effective"
        :route-query="$route.query"
      />
    </div>

    <div v-if="selection.startsWith('product:') && form.booking_product" class="alert alert-info mt-3 mb-0">
      Die produktbasierte Einbindung „{{ form.booking_product.name }}“
      ({{ form.booking_product.function }}) ist für dieses Objekt ausgewählt.
    </div>

    <div v-if="selection === 'custom'" class="alert alert-info mt-3 mb-0">
      Bitte erstellen Sie ein
      <a :href="supportTicketUrl">Ticket</a>
      und beschreiben Sie möglichst genau Ihre Vorstellungen für Ihr Anfrageformular.
    </div>

    <template v-if="form.enabled">
      <div v-if="isNewForm" class="mt-3">
        <label class="form-label" for="inquiry-form-recipient">
          {{ form.recipient_type === 'fax' ? 'Faxnummer' : 'E-Mail-Adresse' }} für die Zustellung
        </label>
        <input
          id="inquiry-form-recipient"
          v-model.trim="form.recipient"
          class="form-control"
          :class="{'is-invalid': recipientEmailInvalid}"
          :type="form.recipient_type === 'fax' ? 'tel' : 'email'"
          required
          placeholder="me@example.com"
        >
        <div v-if="recipientEmailInvalid" class="invalid-feedback">
          Bitte geben Sie eine gültige E-Mail-Adresse ein.
        </div>

        <AccommodationFormConfigurationFields
          v-if="hasAccommodationConfiguration"
          id-prefix="new-inquiry-form"
          class="mt-3"
          :category-input="categoryInput"
          :category-groups="categoryGroups"
          :category-title-suggestions="categoryTitleSuggestions"
          :available-services="availableServices"
          :selected-services="selectedServices"
          @update-category-title="updateCategoryTitle"
          @update-category-option="updateCategoryOption"
          @add-category-group="addCategoryGroup"
          @remove-category-group="removeCategoryGroup"
          @add-category-option="addCategoryOption"
          @remove-category-option="removeCategoryOption"
          @toggle-service="toggleService"
        />
      </div>

      <div class="inquiry-form-config__summary mt-3">
        <div class="inquiry-form-config__summary-header">
          <div>
            <strong>{{ form.display_name }}</strong>
            <span v-if="selectedFormIsPremium" class="badge text-bg-warning ms-2">premium</span>
            <div class="small text-muted">Vorlage: {{ selectedTemplateName }}</div>
            <div class="small text-muted">Zustellung an {{ form.recipient || 'noch keine E-Mail-Adresse' }}</div>
          </div>
          <button type="button" class="btn btn-outline-primary" @click="openSettings">
            <span aria-hidden="true">⚙</span> Einstellungen
          </button>
        </div>
        <div class="mt-3">
          <p class="mb-0">
            Interessierte geben ihre Kontaktdaten und ihre Anfrage ein und senden das Formular ab.
            Die Anfrage wird auf Spam geprüft und anschließend an Sie zugestellt.<br>
            Die anfragende Person erhält eine Kopie mit dem Hinweis:
            „Vielen Dank für Ihre Anfrage von [Datum]. {{ form.contact_name || 'Ihre Location' }} wird sich mit Ihnen in Verbindung setzen.“
          </p>
        </div>
      </div>

      <div v-if="sharedObjects.length" class="alert alert-warning mt-3 mb-0">
        Dieses Anfrageformular wird auch von {{ sharedObjects.map(object => object.name).join(', ') }} verwendet.
        Änderungen an den Einstellungen wirken auf alle verbundenen Objekte.
      </div>

      <div v-if="selectedFormIsPremium && !form.premium_effective" class="alert alert-warning mt-3 mb-0">
        Das ausgewählte Formular kann nur mit Premiumpaket verwendet werden.
        Für Ihr Objekt wird automatisch das Formular "Mitteilung" aktiviert.
      </div>

      <div class="d-flex flex-wrap gap-2 mt-3">
        <a v-if="form.preview_url && !isNewForm" :href="form.preview_url" target="_blank" class="btn btn-outline-secondary">Vorschau</a>
        <a
          v-if="!isNewForm && !form.premium_effective && form.premium_preview_url"
          :href="form.premium_preview_url"
          target="_blank"
          class="btn btn-outline-warning"
        >Premium-Vorschau</a>
      </div>
    </template>
  </fieldset>

  <Dialog
    v-model:visible="settingsOpen"
    modal
    class="inquiry-form-dialog"
    header="Einstellungen des Anfrageformulars"
    :style="{width: '95vw', maxWidth: '760px'}"
  >
    <div class="row g-3">
      <div class="col-12">
        <label class="form-label" for="inquiry-form-display-name">Bezeichnung</label>
        <input id="inquiry-form-display-name" v-model.trim="form.display_name" class="form-control" required>
      </div>

      <div class="col-12 col-md-4">
        <label class="form-label" for="inquiry-form-recipient-type">Empfängerart</label>
        <select id="inquiry-form-recipient-type" v-model="form.recipient_type" class="form-select">
          <option value="email">E-Mail</option>
          <option v-if="form.fax_allowed || form.recipient_type === 'fax'" value="fax" :disabled="!form.fax_allowed">Fax</option>
        </select>
        <small v-if="!form.fax_allowed" class="text-muted">Fax ist für diesen Kunden nicht freigeschaltet.</small>
      </div>

      <div class="col-12 col-md-8">
        <label class="form-label" for="inquiry-form-settings-recipient">
          {{ form.recipient_type === 'fax' ? 'Faxnummer' : 'E-Mail-Adresse' }}
        </label>
        <input
          id="inquiry-form-settings-recipient"
          v-model.trim="form.recipient"
          :type="form.recipient_type === 'fax' ? 'tel' : 'email'"
          class="form-control"
          :class="{'is-invalid': recipientEmailInvalid}"
          required
        >
        <div v-if="recipientEmailInvalid" class="invalid-feedback">
          Bitte geben Sie eine gültige E-Mail-Adresse ein.
        </div>
      </div>

      <div class="col-12">
        <label class="form-label" for="inquiry-form-output-format">Ausgabeformat</label>
        <select id="inquiry-form-output-format" v-model="form.output_format" class="form-select">
          <option value="html">HTML-Format</option>
          <option value="easy_camping_ec3" :disabled="form.recipient_type === 'fax'">EasyCamping EC3-Format</option>
        </select>
      </div>

      <div class="col-12">
        <label class="form-label" for="inquiry-form-subject">
          Betreff der versendeten Anfrage <span class="badge text-bg-warning">premium</span>
        </label>
        <input
          id="inquiry-form-subject"
          v-model.trim="form.message_subject"
          class="form-control"
          type="text"
          :placeholder="form.message_subject_placeholder"
        >
        <small class="text-muted">Wird gespeichert, ist aber nur mit einem Premium-Paket wirksam.</small>
      </div>

      <div v-if="mandatoryContactOptions.length" class="col-12">
        <span class="form-label d-block">Pflichtfelder der Kontaktdaten</span>
        <div class="row g-2">
          <div v-for="field in mandatoryContactOptions" :key="field" class="col-12 col-md-4">
            <label class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                :checked="mandatoryContactFields.includes(field)"
                @change="toggleMandatoryContactField(field, $event.target.checked)"
              >
              {{ contactFieldLabel(field) }}
            </label>
          </div>
        </div>
      </div>

      <div v-if="showOfferIntegrationOption" class="col-12">
        <label class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            :checked="integrateOffers"
            @change="toggleOfferIntegration($event.target.checked)"
          >
          Angebote automatisch integrieren
        </label>
        <small class="text-muted">
          Die für dieses Objekt verfügbaren Angebote werden automatisch in das Anfrageformular übernommen.
        </small>
      </div>

      <AccommodationFormConfigurationFields
        v-if="hasAccommodationConfiguration"
        id-prefix="inquiry-form-settings"
        class="col-12"
        :category-input="categoryInput"
        :category-groups="categoryGroups"
        :category-title-suggestions="categoryTitleSuggestions"
        :available-services="availableServices"
        :selected-services="selectedServices"
        @update-category-title="updateCategoryTitle"
        @update-category-option="updateCategoryOption"
        @add-category-group="addCategoryGroup"
        @remove-category-group="removeCategoryGroup"
        @add-category-option="addCategoryOption"
        @remove-category-option="removeCategoryOption"
        @toggle-service="toggleService"
      />

      <div class="col-12 small text-muted">
        Kontaktdaten im Formular werden automatisch aus der Objekt-Location übernommen. Telefonnummer und E-Mail werden nur mit Premium ausgegeben.
      </div>
    </div>

    <template #footer>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="recipientEmailInvalid"
        @click="settingsOpen = false"
      >Einstellungen übernehmen</button>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog'
import PremiumFeatureHint from '@/components/base_type/PremiumFeatureHint.vue'
import AccommodationFormConfigurationFields from '@/components/objects/AccommodationFormConfigurationFields.vue'

const DEFAULT_MESSAGE_SUBJECT = 'Kontakt aus dem Tourismusportal www.mvp.de der MANET Marketing GmbH'
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+$/

const clone = value => JSON.parse(JSON.stringify(value))
const isMvpSelection = selection => selection.startsWith('route:')
  || selection.startsWith('template:')
  || selection === 'custom'
const selectionFor = (form, bookingMode, bookingProductConfigId) => {
  if (bookingMode === 'external_url') return 'external'
  if (bookingMode === 'product' && bookingProductConfigId) return `product:${bookingProductConfigId}`
  if (bookingMode !== 'form6mvp' || !form.enabled) return 'none'
  if (form.submission_route_id) return `route:${form.submission_route_id}`
  if (form.form_template_id) return `template:${form.form_template_id}`
  return 'none'
}

export default {
  components: {AccommodationFormConfigurationFields, Dialog, PremiumFeatureHint},
  props: {
    modelValue: {type: Object, required: true},
    bookingMode: {type: String, default: 'none'},
    bookingProductConfigId: {type: [String, Number], default: null},
    externalBookingUrl: {type: String, default: ''},
    externalBookingSystem: {type: String, default: ''},
  },
  emits: [
    'update:modelValue',
    'update:bookingMode',
    'update:bookingProductConfigId',
    'update:externalBookingUrl',
  ],
  data() {
    const form = {
      enabled: false,
      submission_route_id: null,
      form_template_id: null,
      display_name: '',
      recipient_type: 'email',
      recipient: '',
      output_format: 'html',
      message_subject: DEFAULT_MESSAGE_SUBJECT,
      message_subject_placeholder: DEFAULT_MESSAGE_SUBJECT,
      form_definition: {},
      fax_allowed: false,
      has_integratable_offers: false,
      shared_objects: [],
      available_submission_routes: [],
      available_form_templates: [],
      available_booking_systems: [],
      available_booking_products: [],
      booking_product: null,
      managed_by_manet: false,
      ...clone(this.modelValue),
    }
    const selection = selectionFor(form, this.bookingMode, this.bookingProductConfigId)

    return {
      form,
      selection,
      lastMvpSelection: isMvpSelection(selection) ? selection : null,
      externalUrl: this.externalBookingUrl || '',
      externalSystem: this.externalBookingSystem || '',
      showStoredExternalSystem: true,
      settingsOpen: false,
    }
  },
  computed: {
    isMvpFormSelection() {
      return isMvpSelection(this.selection)
    },
    isNewForm() {
      return this.selection.startsWith('template:')
    },
    selectedTemplate() {
      return this.form.available_form_templates.find(template => String(template.id) === String(this.form.form_template_id))
    },
    selectedRoute() {
      if (!this.selection.startsWith('route:')) return null

      const routeId = this.selection.split(':')[1]
      return this.form.available_submission_routes.find(route => String(route.id) === routeId)
    },
    selectedFormIsPremium() {
      return Boolean(this.selectedTemplate?.premium || this.selectedRoute?.premium)
    },
    selectedTemplateName() {
      return this.selectedTemplate?.name || this.selectedRoute?.form_template_name || 'Keine Vorlage zugeordnet'
    },
    hasAccommodationConfiguration() {
      return Boolean(this.categoryInput || this.availableServices.length)
    },
    recipientEmailInvalid() {
      const recipient = String(this.form.recipient || '')
      return this.form.recipient_type === 'email' && recipient.length > 0 && !EMAIL_PATTERN.test(recipient)
    },
    accommodationStep() {
      return (this.form.form_definition?.steps || []).find(step => step.module === 'accommodation')
    },
    contactStep() {
      return (this.form.form_definition?.steps || []).find(step => step.module === 'contact')
    },
    traveldateOfferStep() {
      return (this.form.form_definition?.steps || []).find(step => step.module === 'traveldateoffer')
    },
    mandatoryContactOptions() {
      return this.selectedTemplate?.configuration_options?.completion?.contact?.mandatory?.options || []
    },
    mandatoryContactFields() {
      return this.contactStep?.mandatory || []
    },
    categoryGroups() {
      return this.accommodationStep?.categories || []
    },
    groupedCategories() {
      return this.categoryInput === 'grouped_categories'
    },
    categoryInput() {
      return this.selectedTemplate?.configuration_options?.completion?.categories?.input
    },
    categoryTitleSuggestions() {
      if (this.selectedTemplate?.key !== 'accommodation') return []

      return ['Zimmer', 'Ferienwohnung', 'Ferienhaus', 'Unterkunft']
    },
    availableServices() {
      return this.selectedTemplate?.configuration_options?.completion?.services?.options || []
    },
    selectedServices() {
      return (this.accommodationStep?.services || []).map(service => service.title)
    },
    showOfferIntegrationOption() {
      return this.selectedFormIsPremium && this.form.has_integratable_offers && Boolean(this.traveldateOfferStep)
    },
    integrateOffers() {
      return this.form.form_definition?.integrateOffers !== false
    },
    sharedObjects() {
      return (this.form.shared_objects || []).filter(object => String(object.id) !== String(this.form.object_id))
    },
    supportTicketUrl() {
      const objectName = this.form.object_name || this.form.contact_name || 'Objekt'
      const title = `S${this.form.object_id} ${objectName} - Frage zum Buchungssystem`
      return `/account/support/new?title=${encodeURIComponent(title)}`
    },
  },
  watch: {
    modelValue: {
      deep: true,
      handler(value) {
        if (JSON.stringify(value) === JSON.stringify(this.form)) return

        this.form = clone(value)
        this.selection = selectionFor(this.form, this.bookingMode, this.bookingProductConfigId)
        if (this.isMvpFormSelection) this.lastMvpSelection = this.selection
      },
    },
    form: {
      deep: true,
      handler(value) {
        this.$emit('update:modelValue', clone(value))
      },
    },
    'form.recipient_type'(value) {
      if (value === 'fax' && this.form.output_format === 'easy_camping_ec3') this.form.output_format = 'html'
    },
    bookingMode(value) {
      if (this.selection === 'custom' && value === 'none') return

      this.selection = selectionFor(this.form, value, this.bookingProductConfigId)
      if (this.isMvpFormSelection) this.lastMvpSelection = this.selection
    },
    bookingProductConfigId(value) {
      this.selection = selectionFor(this.form, this.bookingMode, value)
    },
    externalBookingUrl(value) {
      if (value !== this.externalUrl) {
        this.externalUrl = value || ''
        this.$nextTick(() => { this.showStoredExternalSystem = true })
      }
    },
    externalBookingSystem(value) {
      if (value !== this.externalSystem) {
        this.externalSystem = value || ''
        this.showStoredExternalSystem = true
      }
    },
    externalUrl(value) {
      this.showStoredExternalSystem = false
      this.$emit('update:externalBookingUrl', value)
    },
  },
  methods: {
    applySelection() {
      if (this.selection === 'none' || this.selection === 'custom') {
        Object.assign(this.form, {enabled: false, submission_route_id: null})
        this.$emit('update:bookingMode', 'none')
        this.$emit('update:bookingProductConfigId', null)
        if (this.selection === 'custom') this.lastMvpSelection = this.selection
        return
      }

      if (this.selection === 'external') {
        Object.assign(this.form, {enabled: false, submission_route_id: null})
        this.$emit('update:bookingMode', 'external_url')
        this.$emit('update:bookingProductConfigId', null)
        return
      }

      if (this.selection.startsWith('product:')) {
        const productId = this.selection.split(':')[1]
        Object.assign(this.form, {enabled: false, submission_route_id: null})
        this.$emit('update:bookingMode', 'product')
        this.$emit('update:bookingProductConfigId', Number(productId))
        return
      }

      this.lastMvpSelection = this.selection
      this.$emit('update:bookingMode', 'form6mvp')
      this.$emit('update:bookingProductConfigId', null)
      const [kind, id] = this.selection.split(':')
      if (kind === 'route') {
        const route = this.form.available_submission_routes.find(item => String(item.id) === id)
        if (route) Object.assign(this.form, clone(route), {enabled: true, submission_route_id: route.id})
        return
      }

      const template = this.form.available_form_templates.find(item => String(item.id) === id)
      if (!template) return

      Object.assign(this.form, {
        enabled: true,
        submission_route_id: null,
        form_template_id: template.id,
        display_name: template.name,
        recipient_type: 'email',
        recipient: '',
        output_format: 'html',
        message_subject: DEFAULT_MESSAGE_SUBJECT,
        form_definition: clone(template.form_definition),
        shared_objects: [],
      })
      this.ensureAccommodationGroups()
    },
    selectMvpFormMode() {
      const routeSelectionAvailable = value => {
        if (!value?.startsWith('route:')) return false
        const id = value.split(':')[1]
        return this.form.available_submission_routes.some(route => String(route.id) === id)
      }
      const templateSelectionAvailable = value => {
        if (!value?.startsWith('template:')) return false
        const id = value.split(':')[1]
        return this.form.available_form_templates.some(template => String(template.id) === id)
      }

      let nextSelection = this.lastMvpSelection
      if (!routeSelectionAvailable(nextSelection)
        && !templateSelectionAvailable(nextSelection)
        && nextSelection !== 'custom') {
        const defaultTemplate = this.form.available_form_templates.find(template => template.key === 'simple_message')
          || this.form.available_form_templates.find(template => !template.premium)
          || this.form.available_form_templates[0]
        const existingRoute = this.form.available_submission_routes[0]
        nextSelection = defaultTemplate
          ? `template:${defaultTemplate.id}`
          : (existingRoute ? `route:${existingRoute.id}` : 'custom')
      }

      this.selection = nextSelection
      this.applySelection()
    },
    routeTypeLabel(route) {
      return {
        simple_message: 'Mitteilung',
        accommodation: 'Unterkunft',
        accommodation_single: 'Unterkunft (Einzelunterkunft ohne Objekt-/Kategorieauswahl)',
        accommodation_rentals: 'Unterkunft',
        table_reservation: 'Tischreservierung',
      }[route.form_template_key] || route.display_name
    },
    templateDescription(template) {
      return {
        simple_message: 'Für allgemeine Nachrichten und individuelle Anliegen.',
        accommodation: 'Mit Reisezeitraum, Personenzahl und Auswahl der Unterkunft.',
        accommodation_single: 'Direkte Anfrage für genau diese Unterkunft.',
        accommodation_rentals: 'Mit Reisezeitraum, Personenzahl und Auswahl des Mietobjekts.',
        table_reservation: 'Für Reservierungswünsche mit Datum, Uhrzeit und Personenzahl.',
      }[template.key] || 'Konfigurieren Sie ein passendes Anfrageformular für dieses Objekt.'
    },
    templateIcon(template) {
      return {
        simple_message: 'fa-pro-solid:envelope',
        accommodation: 'fa-pro-solid:bed-front',
        accommodation_single: 'fa-pro-solid:house',
        accommodation_rentals: 'fa-pro-solid:key',
        table_reservation: 'fa-pro-solid:utensils',
      }[template.key] || 'fa-pro-solid:rectangle-list'
    },
    openSettings() {
      this.ensureAccommodationGroups()
      this.settingsOpen = true
    },
    ensureAccommodationGroups() {
      if (!this.categoryInput || !this.accommodationStep) return
      if (this.categoryGroups.length) return

      this.accommodationStep.categories = this.groupedCategories
        ? [
            {title: 'Stellplatz hinzufügen', options: ['']},
            {title: 'Mietobjekt hinzufügen', options: ['']},
          ]
        : [{title: 'Zimmerkategorien', options: ['']}]
    },
    addCategoryGroup() {
      this.accommodationStep.categories.push({title: 'Weitere Auswahl', options: ['']})
    },
    updateCategoryTitle(groupIndex, value) {
      this.categoryGroups[groupIndex].title = value
    },
    removeCategoryGroup(groupIndex) {
      this.accommodationStep.categories.splice(groupIndex, 1)
    },
    addCategoryOption(groupIndex) {
      this.categoryGroups[groupIndex].options.push('')
    },
    updateCategoryOption(groupIndex, optionIndex, value) {
      this.categoryGroups[groupIndex].options[optionIndex] = value
    },
    removeCategoryOption(groupIndex, optionIndex) {
      this.categoryGroups[groupIndex].options.splice(optionIndex, 1)
    },
    toggleService(service, checked) {
      if (!this.accommodationStep) return

      const services = new Set(this.selectedServices)
      if (checked) services.add(service)
      else services.delete(service)
      this.accommodationStep.services = Array.from(services).map(title => ({title}))
    },
    contactFieldLabel(field) {
      return {
        phone: 'Telefon',
        email: 'E-Mail-Adresse',
        address: 'Anschrift',
      }[field] || field
    },
    toggleMandatoryContactField(field, checked) {
      if (!this.contactStep) return

      const mandatory = new Set(this.mandatoryContactFields)
      if (checked) mandatory.add(field)
      else mandatory.delete(field)
      this.contactStep.mandatory = this.mandatoryContactOptions.filter(option => mandatory.has(option))
    },
    toggleOfferIntegration(checked) {
      this.form.form_definition.integrateOffers = checked
    },
  },
}
</script>

<style scoped>
.inquiry-form-config__intro h3,
.inquiry-form-config__section-heading h4 {
  color: #26364a;
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0;
}

.inquiry-form-config__intro p,
.inquiry-form-config__section-heading p {
  color: #5f6b7a;
  margin: .25rem 0 0;
}

.inquiry-form-config__choice-group {
  display: grid;
  gap: .75rem;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  margin-top: 1rem;
}

.inquiry-form-choice {
  align-items: start;
  background: rgba(255, 255, 255, .9);
  border: 1px solid #cbd3dc;
  border-radius: 0;
  box-shadow: 0 .15rem .45rem rgba(38, 54, 74, .06);
  cursor: pointer;
  display: grid;
  gap: .75rem;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-height: 7.25rem;
  padding: 1rem;
  position: relative;
  transition: border-color .15s ease, box-shadow .15s ease, transform .15s ease;
}

.inquiry-form-choice:hover {
  border-color: var(--base-type-primary, #1b6c97);
  box-shadow: 0 .35rem .8rem rgba(38, 54, 74, .11);
  transform: translateY(-1px);
}

.inquiry-form-config:disabled .inquiry-form-choice {
  cursor: not-allowed;
  opacity: .72;
}

.inquiry-form-config:disabled .inquiry-form-choice:hover {
  border-color: #cbd3dc;
  box-shadow: 0 .15rem .45rem rgba(38, 54, 74, .06);
  transform: none;
}

.inquiry-form-choice:focus-within {
  outline: 3px solid color-mix(in srgb, var(--base-type-primary, #1b6c97) 25%, transparent);
  outline-offset: 2px;
}

.inquiry-form-choice--selected {
  background: color-mix(in srgb, var(--base-type-primary, #1b6c97) 7%, #fff);
  border-color: var(--base-type-primary, #1b6c97);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--base-type-primary, #1b6c97) 20%, transparent);
}

.inquiry-form-choice__icon,
.inquiry-form-config__section-icon {
  align-items: center;
  background: color-mix(in srgb, var(--base-type-primary, #1b6c97) 11%, #fff);
  color: var(--base-type-primary, #1b6c97);
  display: inline-flex;
  flex: 0 0 auto;
  height: 2.65rem;
  justify-content: center;
  width: 2.65rem;
}

.inquiry-form-choice__icon {
  border-radius: .65rem;
  font-size: 1.1rem;
}

.inquiry-form-choice__content {
  display: flex;
  flex-direction: column;
  gap: .4rem;
  min-width: 0;
}

.inquiry-form-choice__title {
  align-items: center;
  color: #26364a;
  display: flex;
  flex-wrap: wrap;
  font-weight: 700;
  gap: .4rem;
  line-height: 1.25;
}

.inquiry-form-choice__description {
  color: #5f6b7a;
  font-size: .875rem;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.inquiry-form-choice__check {
  color: #aab3bd;
  font-size: 1.15rem;
  margin-top: .15rem;
}

.inquiry-form-choice--selected .inquiry-form-choice__check {
  color: var(--base-type-primary, #1b6c97);
}

.inquiry-form-config__mvp-forms {
  background: rgba(255, 255, 255, .55);
  border: 1px solid color-mix(in srgb, var(--base-type-primary, #1b6c97) 24%, #d7dde4);
  border-radius: 0;
  margin-top: 1.25rem;
  padding: 1.25rem;
}

.inquiry-form-config__section-heading {
  align-items: center;
  display: flex;
  gap: .85rem;
}

.inquiry-form-config__section-icon {
  border-radius: 50%;
  font-size: 1.05rem;
}

.inquiry-form-config__group-label {
  color: #49586a;
  font-size: .78rem;
  font-weight: 800;
  letter-spacing: .04em;
  margin-top: 1.25rem;
  text-transform: uppercase;
}

.inquiry-form-config__choice-group--forms {
  grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
  margin-top: .6rem;
}

.inquiry-form-choice--form {
  min-height: 6.6rem;
}

.inquiry-form-config__details {
  background: rgba(255, 255, 255, .7);
  border-left: .25rem solid var(--base-type-primary, #1b6c97);
  border-radius: .5rem;
  padding: 1rem;
}

.inquiry-form-config__summary {
  border: 1px solid var(--bs-border-color);
  padding: 1rem;
}

.inquiry-form-config__summary-header {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.inquiry-form-config :deep(.form-control),
.inquiry-form-config :deep(.form-select),
.inquiry-form-config :deep(.form-check-input),
.inquiry-form-config :deep(.btn),
.inquiry-form-config :deep(.badge),
.inquiry-form-config :deep(.alert),
.inquiry-form-config__summary { border-radius: 0 !important; }

.inquiry-form-choice .badge {
  border-radius: 999px !important;
  font-size: .68rem;
  letter-spacing: .01em;
  padding: .3rem .5rem;
}

:global(.inquiry-form-dialog),
:global(.inquiry-form-dialog .p-dialog-header),
:global(.inquiry-form-dialog .p-dialog-content),
:global(.inquiry-form-dialog .p-dialog-footer),
:global(.inquiry-form-dialog .form-control),
:global(.inquiry-form-dialog .form-select),
:global(.inquiry-form-dialog .btn),
:global(.inquiry-form-dialog .badge) { border-radius: 0 !important; }

@media (max-width: 575.98px) {
  .inquiry-form-config__choice-group,
  .inquiry-form-config__choice-group--forms {
    grid-template-columns: 1fr;
  }

  .inquiry-form-config__mvp-forms {
    padding: 1rem;
  }

  .inquiry-form-choice {
    min-height: 0;
  }

  .inquiry-form-config__summary-header {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
